import React, { useMemo, SyntheticEvent } from 'react';
import { Interpreter } from 'xstate';
import { useService } from '@xstate/react';
import { PMCConfig, ProviderMatchProviderV9 } from '@kyruus/types';
import { FormattedMessage } from '@kyruus/intl';
import { ButtonWithSpinner } from '@kyruus/button';

import { AvailabilityProps, Log, SearchSummary } from 'Src/types';
import {
  LocationContext,
  LocationEvent,
  LocationStateSchema,
  EVENTS,
  STATES
} from 'Src/machines/location-v9/types';
import { FROM_LOCATIONS, ProviderListV9 } from 'Src/shared/ProviderListV9';
import { getApptOptions } from 'Src/utils/map-search';
import LoadingOverlay from 'Src/shared/loading';
import messages from '../messages';
import { LOGGING_EVENTS } from '../constants';
import { ProvidersContainer } from './styles';
import {
  getConfigProperty,
  shouldRenderDirectBookInDrawer
} from 'Common/config';
import { useHistory } from 'react-router-dom';

interface ProvidersListProps {
  config: PMCConfig;
  customerCode: string;
  locationId: string;
  log: Log;
  productName: string;
  searchSummary: SearchSummary;
  service: Interpreter<LocationContext, LocationStateSchema, LocationEvent>;
}

export const ProvidersList = ({
  config,
  customerCode,
  locationId,
  log,
  productName,
  searchSummary,
  service,
  appointmentInfo = {},
  slots = {},
  slotsStatus = {},
  purposeOptionsByProviderId = {},
  availabilityError = '',
  purposesLoading = false,
  fetchProviderAvailability,
  fetchPurposesByProviderId
}: ProvidersListProps & AvailabilityProps) => {
  const isModernCardExperience: boolean =
    getConfigProperty(config, 'provider_tile.type', 'modern') === 'modern';
  const [state, send] = useService(service);
  const history = useHistory();

  const { providers, totalProviders, location } = state.context;
  const providersLocationId = providers?.[0]?.locations?.[0]?.id;

  const filteredSlotsByProviderId = useMemo(() => {
    if (!slots || !providersLocationId) {
      return slots;
    }

    const filtered: typeof slots = {};
    Object.entries(slots).forEach(([providerId, slotsInfo]) => {
      if (slotsInfo?.slots) {
        const filteredSlots = slotsInfo.slots.filter(
          (slot) =>
            isModernCardExperience ||
            String(slot.location_id) === String(providersLocationId)
        );

        filtered[providerId] = {
          ...slotsInfo,
          slots: filteredSlots
        };
      } else {
        filtered[providerId] = slotsInfo;
      }
    });

    return filtered;
  }, [slots, providersLocationId, isModernCardExperience]);

  if (!providers || !totalProviders) {
    return null;
  }

  const handleClickLoadMore = () => {
    log(LOGGING_EVENTS.CLICK_LOAD_MORE_PROVIDERS, { locationId });
    send(EVENTS.loadMoreProviders);
  };

  const handleTimeSlotsClick = (
    _: SyntheticEvent,
    data: {
      providerId: number;
      date?: string;
      time?: string;
      href?: string;
      unformattedDate?: string;
      locationId?: string;
      patient_relationship?: string;
      [key: string]: unknown;
    }
  ) => {
    const { date, time, providerId, href } = data;
    const provider = providers.find((p) => p.id === providerId);
    log(LOGGING_EVENTS.CLICK_LOCATIONS_PROVIDER_TILE, {
      provider_id: provider?.id,
      action: 'time_slots',
      date: date,
      time: time
    });

    if (shouldRenderDirectBookInDrawer(config) && !!provider) {
      if (!location || !href) {
        return;
      }
      history.push(href, {
        provider: {
          location,
          provider
        }
      });
    }
  };

  const handleViewMoreClick = (
    _: SyntheticEvent,
    data: {
      providerId?: number;
      viewMoreBookOnlineUrl?: string;
      [key: string]: unknown;
    }
  ) => {
    const { providerId, viewMoreBookOnlineUrl } = data;
    const provider = providers.find((p) => p.id === providerId);
    log(LOGGING_EVENTS.CLICK_LOCATIONS_PROVIDER_TILE, {
      provider_id: providerId,
      action: 'view_more_appointments'
    });

    if (shouldRenderDirectBookInDrawer(config) && provider) {
      const locationData = state.context.location;
      if (!locationData || !viewMoreBookOnlineUrl) {
        return;
      }

      history.push(viewMoreBookOnlineUrl, {
        provider: {
          location,
          provider
        }
      });
    }
  };

  const loading = state.matches({ [STATES.providers]: STATES.pending });
  const showMoreButton = providers?.length < totalProviders;

  return (
    <ProvidersContainer data-testid="provider-list">
      <LoadingOverlay loading={loading} />
      <ProviderListV9
        apptOptions={getApptOptions(providers, locationId)}
        config={config}
        providers={providers}
        customerCode={customerCode}
        productName={productName}
        searchSummary={searchSummary}
        log={log}
        profileSummaryParams={{
          from: FROM_LOCATIONS,
          location_id: locationId
        }}
        providerCallbackFunc={(provider: ProviderMatchProviderV9) =>
          log(LOGGING_EVENTS.CLICK_LOCATIONS_PROVIDER_TILE, {
            provider_id: provider.id
          })
        }
        bookingCallbackFunc={(provider: ProviderMatchProviderV9) =>
          log(LOGGING_EVENTS.CLICK_LOCATIONS_PROVIDER_TILE_BOOK_APPT, {
            provider_id: provider.id
          })
        }
        showProviderLocationsInProviderTile={false}
        appointmentInfo={appointmentInfo}
        slots={filteredSlotsByProviderId}
        slotsStatus={slotsStatus}
        purposeOptionsByProviderId={purposeOptionsByProviderId}
        availabilityError={availabilityError}
        purposesLoading={purposesLoading}
        fetchProviderAvailability={fetchProviderAvailability}
        fetchPurposesByProviderId={fetchPurposesByProviderId}
        onTimeSlotsClick={handleTimeSlotsClick}
        onViewMoreClick={handleViewMoreClick}
      />
      {showMoreButton && (
        <FormattedMessage {...messages.showMoreProvidersButtonLabel}>
          {(text) => (
            <ButtonWithSpinner
              onClick={handleClickLoadMore}
              mode="secondary"
              showSpinner={loading}
            >
              {text}
            </ButtonWithSpinner>
          )}
        </FormattedMessage>
      )}
    </ProvidersContainer>
  );
};

ProvidersList.displayName = 'ProvidersList';
